import React, { useState } from 'react';
import { Col, Row } from '@/lib/utils';
import { useLockerHistory } from '@/features/locker/hooks';
import { formatLockNumber } from '@/features/locker/lockerDisplay';
import dayjs from 'dayjs';
import { colors, semanticColors } from '@/lib/colors';
import { LockerEmpty, LockerError, LockerTableLoading } from './LockerStates';

const PAGE_SIZE = 15;

const HEADERS = ['회수 일시', '회원명', '라커 번호', '비밀번호', '상품명', '회수 사유'];

export default function LockerHistoryTab({
    centerId,
    revealAll,
    onToggleReveal,
}: {
    centerId: string;
    revealAll: boolean;
    onToggleReveal: () => void;
}) {
    const [page, setPage] = useState(1);

    /*
     * 구역 셀렉트를 뺐다. 이 화면은 기본 구역 하나만 쓰는 전제로 돌아가고(실측 86개 센터 전부
     * 단일 구역), 화면 어디에도 구역이 노출되지 않는데 여기만 고르게 하면 없는 개념을 묻는 셈이다.
     */
    const { data, isLoading, isError, refetch } = useLockerHistory({
        centerId,
        page,
        limit: PAGE_SIZE,
    });

    const getReasonBadge = (reason: string): { text: string; tone: 'danger' | 'neutral' | 'info' } => {
        if (reason === 'refunded') return { text: '환불', tone: 'danger' };
        if (reason === 'expired') return { text: '만료', tone: 'neutral' };
        if (reason === 'transferred') return { text: '양도', tone: 'info' };
        return { text: '수동 회수', tone: 'info' };
    };

    const results = data?.results ?? [];
    const totalResults = data?.totalResults ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalResults / (data?.limit || PAGE_SIZE)));

    return (
        <Col style={{ gap: 16 }}>
            <Row style={{ alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <Col style={{ gap: 4 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: colors.LAVEL_4 }}>라커 회수 기록</span>
                    {/*
                     * 데이터로는 "비밀번호가 없었다"와 "기록되지 않았다"를 구별할 수 없다.
                     * 비율을 단정하지 않고 있을 수 있는 사실만 말한다.
                     */}
                    <span style={{ fontSize: 12, color: colors.LAVEL_1 }}>
                        회수 시점의 비밀번호가 함께 남습니다. 이전에 회수된 기록에는 비밀번호가 남아 있지 않을 수 있습니다.
                    </span>
                </Col>
                <button
                    type="button"
                    onClick={onToggleReveal}
                    aria-pressed={revealAll}
                    style={{
                        height: 36,
                        padding: '0 14px',
                        borderRadius: 8,
                        border: `1px solid ${revealAll ? colors.PRIMARY_BLUE : colors.WHITE_600}`,
                        backgroundColor: revealAll ? colors.BLUE_50 : colors.white,
                        color: revealAll ? colors.PRIMARY_BLUE : colors.LAVEL_1,
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: 'pointer',
                    }}
                >
                    {revealAll ? '비밀번호 가리기' : '비밀번호 표시'}
                </button>
            </Row>

            {isLoading && <LockerTableLoading />}

            {!isLoading && isError && <LockerError title="회수 기록을 불러오지 못했습니다" onRetry={() => refetch()} />}

            {!isLoading && !isError && results.length === 0 && (
                <LockerEmpty
                    icon="🗂️"
                    title="아직 회수 기록이 없습니다"
                    description="라커를 회수하거나 이용권이 만료·환불되면 이곳에 기록이 쌓입니다."
                />
            )}

            {!isLoading && !isError && results.length > 0 && (
                <>
                    <div
                        style={{
                            width: '100%',
                            border: `1px solid ${colors.WHITE_600}`,
                            borderRadius: 12,
                            overflowX: 'auto',
                            backgroundColor: colors.white,
                        }}
                    >
                        <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse', minWidth: 720 }}>
                            <thead style={{ backgroundColor: colors.GREY, borderBottom: `1px solid ${colors.WHITE_600}` }}>
                                <tr>
                                    {HEADERS.map((header) => (
                                        <th
                                            key={header}
                                            scope="col"
                                            style={{
                                                padding: '12px 16px',
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color: colors.LAVEL_1,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((item) => {
                                    const badge = getReasonBadge(item.releaseReason);
                                    const semantic = semanticColors[badge.tone];

                                    return (
                                        <tr key={item._id + item.releasedAt} style={{ borderBottom: `1px solid ${colors.WHITE_500}` }}>
                                            <td style={{ padding: '14px 16px', fontSize: 13, color: colors.LAVEL_4, whiteSpace: 'nowrap' }}>
                                                {dayjs(item.releasedAt).format('YYYY.MM.DD HH:mm')}
                                            </td>
                                            <td style={{ padding: '14px 16px', fontSize: 13, color: colors.LAVEL_4, fontWeight: 600 }}>
                                                {item.memberName}
                                            </td>
                                            <td style={{ padding: '14px 16px', fontSize: 13, color: colors.LAVEL_4, fontWeight: 700 }}>
                                                {item.lockerNumber ?? '—'}
                                            </td>
                                            <td style={{ padding: '14px 16px', fontSize: 13, color: item.lockNumber ? colors.LAVEL_4 : colors.GREY_200 }}>
                                                {formatLockNumber(item.lockNumber, revealAll)}
                                            </td>
                                            <td style={{ padding: '14px 16px', fontSize: 13, color: colors.LAVEL_1 }}>
                                                {item.productName}
                                            </td>
                                            <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        padding: '3px 8px',
                                                        borderRadius: 4,
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        backgroundColor: semantic.bg,
                                                        color: semantic.text,
                                                        border: `1px solid ${semantic.border}`,
                                                    }}
                                                >
                                                    {badge.text}
                                                </span>
                                                {item.refundAmount !== undefined && item.refundAmount > 0 && (
                                                    <span style={{ marginLeft: 8, fontSize: 11, color: colors.ERROR }}>
                                                        (-{item.refundAmount.toLocaleString()}원)
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <Row style={{ justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 8 }}>
                            <button
                                type="button"
                                disabled={page === 1}
                                onClick={() => setPage((p) => p - 1)}
                                style={{
                                    padding: '6px 14px',
                                    borderRadius: 6,
                                    border: `1px solid ${colors.WHITE_600}`,
                                    backgroundColor: colors.white,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: colors.LAVEL_1,
                                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                                    opacity: page === 1 ? 0.4 : 1,
                                }}
                            >
                                이전
                            </button>
                            <span style={{ fontSize: 13, color: colors.LAVEL_4, fontWeight: 700 }}>{page}</span>
                            <span style={{ fontSize: 12, color: colors.LAVEL_1 }}>/ {totalPages}</span>
                            <button
                                type="button"
                                disabled={page >= totalPages}
                                onClick={() => setPage((p) => p + 1)}
                                style={{
                                    padding: '6px 14px',
                                    borderRadius: 6,
                                    border: `1px solid ${colors.WHITE_600}`,
                                    backgroundColor: colors.white,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: colors.LAVEL_1,
                                    cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                                    opacity: page >= totalPages ? 0.4 : 1,
                                }}
                            >
                                다음
                            </button>
                        </Row>
                    )}
                </>
            )}
        </Col>
    );
}
